import api from './api';
import supabase from './supabaseClient';

const AUTH_ENDPOINTS = {
    LOGIN: 'auth/login',
    LOGOUT: 'auth/logout',
    REGISTER: 'auth/register',
    VERIFY: 'auth/verify',
    USER: 'auth/user',
};

// Register new user
export const register = async (userData) => {
    if (supabase) {
        try {
            console.log('Registering user with Supabase:', userData.email);
            const userType = userData.userTypeId === 1 ? 'Recruiter' : 'Job Seeker';
            const { data, error } = await supabase.auth.signUp({
                email: userData.email,
                password: userData.password,
                options: {
                    data: {
                        first_name: userData.firstName,
                        last_name: userData.lastName,
                        user_type: userType
                    }
                }
            });

            if (error) {
                throw new Error(error.message || 'Registration failed with Supabase.');
            }

            // Detect if the email is already registered. If so, Supabase returns a user object
            // but with an empty identities array to prevent email enumeration.
            if (data.user && data.user.identities && data.user.identities.length === 0) {
                throw new Error('This email is already registered. If you signed up with Google, please sign in with Google or reset your password.');
            }

            return {
                success: true,
                message: 'Registration successful.',
                user: data.user
            };
        } catch (error) {
            console.warn('Supabase registration failed, trying fallback to traditional backend API...', error);
            try {
                const response = await api.post(AUTH_ENDPOINTS.REGISTER, userData);
                
                if (response.data.token) {
                    localStorage.setItem(
                        process.env.REACT_APP_JWT_STORAGE_KEY || 'jwt-token', 
                        response.data.token
                    );
                    localStorage.setItem(
                        process.env.REACT_APP_USER_STORAGE_KEY || 'user-info', 
                        JSON.stringify({
                            userId: response.data.userId,
                            email: response.data.email,
                            firstName: response.data.firstName,
                            lastName: response.data.lastName,
                            userType: response.data.userType,
                        })
                    );
                }
                
                return {
                    success: true,
                    message: 'Registration successful.',
                    user: response.data
                };
            } catch (fallbackError) {
                console.error('Fallback registration also failed:', fallbackError);
                if (fallbackError.response?.data) {
                    throw fallbackError.response.data;
                }
                throw new Error(fallbackError.message || error.message);
            }
        }
    } else {
        // Fallback to traditional local API
        try {
            const response = await api.post(AUTH_ENDPOINTS.REGISTER, userData);
            
            if (response.data.token) {
                localStorage.setItem(
                    process.env.REACT_APP_JWT_STORAGE_KEY || 'jwt-token', 
                    response.data.token
                );
                localStorage.setItem(
                    process.env.REACT_APP_USER_STORAGE_KEY || 'user-info', 
                    JSON.stringify({
                        userId: response.data.userId,
                        email: response.data.email,
                        firstName: response.data.firstName,
                        lastName: response.data.lastName,
                        userType: response.data.userType,
                    })
                );
            }
            
            return {
                success: true,
                message: 'Registration successful.',
                user: response.data
            };
        } catch (error) {
            if (error.response?.data) {
                throw error.response.data;
            }
            throw new Error('Registration failed. Please try again.');
        }
    }
};

// Login user
export const login = async (credentials) => {
    if (supabase) {
        try {
            console.log('Logging in with Supabase:', credentials.email);
            const { data, error } = await supabase.auth.signInWithPassword({
                email: credentials.email,
                password: credentials.password
            });

            if (error) {
                throw new Error(error.message || 'Login failed with Supabase.');
            }

            if (data.session) {
                const token = data.session.access_token;
                // Store token temporarily to allow the next API call to authenticate
                localStorage.setItem(
                    process.env.REACT_APP_JWT_STORAGE_KEY || 'jwt-token', 
                    token
                );

                // Fetch user profile from public.users to get userId
                try {
                    const profileResponse = await api.get(AUTH_ENDPOINTS.USER);
                    const userProfile = profileResponse.data?.data || profileResponse.data;

                    if (userProfile) {
                        localStorage.setItem(
                            process.env.REACT_APP_USER_STORAGE_KEY || 'user-info', 
                            JSON.stringify(userProfile)
                        );
                        
                        return {
                            token: token,
                            userId: userProfile.userId,
                            email: userProfile.email,
                            firstName: userProfile.firstName,
                            lastName: userProfile.lastName,
                            userType: userProfile.userType
                        };
                    }
                } catch (profileErr) {
                    console.error('Error fetching user profile from backend after Supabase login:', profileErr);
                    // Fallback to metadata if backend profile fetch fails
                    const userType = data.user.user_metadata?.user_type || 'Job Seeker';
                    const fallbackUser = {
                        userId: data.user.id, // Fallback to string UUID if backend isn't synced
                        email: data.user.email,
                        firstName: data.user.user_metadata?.first_name || data.user.user_metadata?.given_name || data.user.user_metadata?.name || 'User',
                        lastName: data.user.user_metadata?.last_name || data.user.user_metadata?.family_name || '',
                        userType: userType
                    };
                    localStorage.setItem(
                        process.env.REACT_APP_USER_STORAGE_KEY || 'user-info', 
                        JSON.stringify(fallbackUser)
                    );
                    return {
                        token: token,
                        ...fallbackUser
                    };
                }
            }
            throw new Error('Invalid session response from Supabase.');
        } catch (error) {
            console.warn('Supabase login failed, trying fallback to traditional backend API...', error);
            try {
                console.log('AuthService Fallback: Attempting login for:', credentials.email);
                const response = await api.post(AUTH_ENDPOINTS.LOGIN, credentials);
                
                if (response.data.token) {
                    localStorage.setItem(
                        process.env.REACT_APP_JWT_STORAGE_KEY || 'jwt-token', 
                        response.data.token
                    );
                    localStorage.setItem(
                        process.env.REACT_APP_USER_STORAGE_KEY || 'user-info', 
                        JSON.stringify({
                            userId: response.data.userId,
                            email: response.data.email,
                            firstName: response.data.firstName,
                            lastName: response.data.lastName,
                            userType: response.data.userType,
                        })
                    );
                }
                
                console.log('AuthService Fallback: Login successful');
                return response.data;
            } catch (fallbackError) {
                console.error('Fallback login also failed:', fallbackError);
                let errorMessage = 'Login failed. Please try again.';
                if (fallbackError.response) {
                    const status = fallbackError.response.status;
                    const data = fallbackError.response.data;
                    if (data && data.message) {
                        errorMessage = data.message;
                    } else if (data && data.error) {
                        errorMessage = data.error;
                    } else if (status === 401) {
                        errorMessage = 'Invalid email or password. Please check your credentials and try again.';
                    }
                } else if (fallbackError.request) {
                    errorMessage = 'Unable to connect to the server. Please check your internet connection.';
                } else {
                    errorMessage = fallbackError.message || error.message;
                }
                throw new Error(errorMessage);
            }
        }
    } else {
        // Fallback to traditional local API
        try {
            console.log('AuthService: Attempting login for:', credentials.email);
            const response = await api.post(AUTH_ENDPOINTS.LOGIN, credentials);
            
            if (response.data.token) {
                localStorage.setItem(
                    process.env.REACT_APP_JWT_STORAGE_KEY || 'jwt-token', 
                    response.data.token
                );
                localStorage.setItem(
                    process.env.REACT_APP_USER_STORAGE_KEY || 'user-info', 
                    JSON.stringify({
                        userId: response.data.userId,
                        email: response.data.email,
                        firstName: response.data.firstName,
                        lastName: response.data.lastName,
                        userType: response.data.userType,
                    })
                );
            }
            
            console.log('AuthService: Login successful');
            return response.data;
        } catch (error) {
            console.log('AuthService: Login error caught:', error);
            let errorMessage = 'Login failed. Please try again.';
            if (error.response) {
                const status = error.response.status;
                const data = error.response.data;
                if (data && data.message) {
                    errorMessage = data.message;
                } else if (data && data.error) {
                    errorMessage = data.error;
                } else if (status === 401) {
                    errorMessage = 'Invalid email or password. Please check your credentials and try again.';
                }
            } else if (error.request) {
                errorMessage = 'Unable to connect to the server. Please check your internet connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred.';
            }
            throw new Error(errorMessage);
        }
    }
};

// Logout user
export const logout = async () => {
    try {
        if (supabase) {
            await supabase.auth.signOut();
        } else {
            await api.post(AUTH_ENDPOINTS.LOGOUT);
        }
    } catch (error) {
        console.error('Logout API call failed:', error);
    } finally {
        localStorage.removeItem(process.env.REACT_APP_JWT_STORAGE_KEY || 'jwt-token');
        localStorage.removeItem(process.env.REACT_APP_USER_STORAGE_KEY || 'user-info');
    }
};

// Verify token
export const verifyToken = async () => {
    try {
        const response = await api.get(AUTH_ENDPOINTS.VERIFY);
        return response.data;
    } catch (error) {
        throw error.response?.data || new Error('Token verification failed');
    }
};

// Get current user
export const getCurrentUser = async () => {
    try {
        const response = await api.get(AUTH_ENDPOINTS.USER);
        return response.data?.data || response.data;
    } catch (error) {
        throw error.response?.data || new Error('Failed to get user info');
    }
};

// Check if user is authenticated
export const isAuthenticated = () => {
    const token = localStorage.getItem(process.env.REACT_APP_JWT_STORAGE_KEY || 'jwt-token');
    return !!token;
};

// Get stored user info
export const getStoredUser = () => {
    const userInfo = localStorage.getItem(process.env.REACT_APP_USER_STORAGE_KEY || 'user-info');
    return userInfo ? JSON.parse(userInfo) : null;
};

// Start OAuth login with a provider (google or linkedin_oidc)
export const loginWithProvider = async (provider, selectedRole) => {
    if (!supabase) {
        throw new Error('Supabase client is not configured.');
    }
    
    // Save selected role locally to apply it after redirect
    if (selectedRole) {
        localStorage.setItem('oauth_selected_role', selectedRole);
    }
    
    const { data, error } = await supabase.auth.signInWithOAuth({
        provider: provider,
        options: {
            redirectTo: window.location.origin + '/login' // redirects back to login page to handle session callback
        }
    });
    
    if (error) throw error;
    return data;
};

// Sync OAuth role with backend
export const syncOAuthRole = async (role) => {
    const response = await api.post(`/auth/oauth-role?role=${role}`);
    return response.data;
};

// Validate email existence
export const validateEmail = async (email) => {
    try {
        const response = await api.get(`auth/validate-email?email=${encodeURIComponent(email)}`);
        return response.data;
    } catch (error) {
        console.error('Email validation failed, bypassing...', error);
        return { success: true, data: true }; // Bypass on error to not block user
    }
};

// Set password for OAuth linked accounts
export const setOAuthPassword = async (password) => {
    // 1. Update password in Supabase Auth if session exists
    if (supabase) {
        try {
            const { error: supabaseError } = await supabase.auth.updateUser({ password });
            if (supabaseError) {
                console.warn('Supabase password update failed, proceeding with backend sync...', supabaseError);
            }
        } catch (err) {
            console.warn('Supabase update exception, proceeding with backend sync...', err);
        }
    }
    
    // 2. Update password in public.users backend database
    const response = await api.put('auth/set-password', { password });
    return response.data;
};

export const authService = {
    register,
    login,
    logout,
    verifyToken,
    getCurrentUser,
    isAuthenticated,
    getStoredUser,
    loginWithProvider,
    syncOAuthRole,
    validateEmail,
    setOAuthPassword
};

export default authService;